/**
 * Shared cut-list import/export for single-cutlist panel builders.
 * Multi-colour builders (MFC / compact) keep their inline implementations.
 *
 * Usage:
 *   PBCutlistIo.wireSingle({
 *     tbody, makeRow, bindRow, afterChange,
 *     hasEdging: true|false,
 *     filePrefix: 'mdf-cutlist',
 *     defaultEdge: '4'
 *   });
 */
(function (global) {
  'use strict';

  const EDGE_CODE_LABEL = {
    '0': 'No edging',
    '1': '1 long edge',
    '2': '2 long edges',
    '3': '1 short edge',
    '4': 'All edges',
    '5': '2 long + 1 short',
    '6': '2 short edges',
    '7': '1 long + 2 short'
  };

  function toInt(v, fallback) {
    const n = parseInt(String(v ?? '').trim(), 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function normalizeEdgeCodeForImport(v) {
    const s = String(v ?? '').trim();
    if (!s) return '4';
    if (/^\d+$/.test(s)) {
      const n = parseInt(s, 10);
      if (n >= 0 && n <= 7) return String(n);
    }
    const t = s.toLowerCase();
    if (/^none$|^no\s*edging?$|^no\s*edge/.test(t)) return '0';
    if (/all|every|four|4\s*side|full\s*perim/.test(t)) return '4';
    if (/2\s*long.*1\s*short|2l\s*\+?\s*1s/.test(t)) return '5';
    if (/1\s*long.*2\s*short|1l\s*\+?\s*2s/.test(t)) return '7';
    if (/2\s*long/.test(t)) return '2';
    if (/1\s*long/.test(t)) return '1';
    if (/2\s*short/.test(t)) return '6';
    if (/1\s*short/.test(t)) return '3';
    return '4';
  }

  function parseNumericMm(v) {
    let s = String(v ?? '').trim();
    if (!s) return 0;
    s = s.replace(/mm/gi, '').replace(/\s+/g, ' ').trim();
    s = s.replace(/(\d)[,](\d{3})\b/g, '$1$2');
    s = s.replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? Math.round(n) : 0;
  }

  function parseDelimitedLine(line, delim) {
    const out = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQ = !inQ;
        continue;
      }
      if (!inQ && c === delim) {
        out.push(cur.trim());
        cur = '';
        continue;
      }
      cur += c;
    }
    out.push(cur.trim());
    return out;
  }

  function detectDelimiter(line) {
    let inQ = false;
    const counts = { '\t': 0, '|': 0, ';': 0, ',': 0 };
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQ = !inQ;
        continue;
      }
      if (!inQ && Object.prototype.hasOwnProperty.call(counts, ch)) counts[ch]++;
    }
    let best = ',';
    let mx = -1;
    Object.keys(counts).forEach(k => {
      if (counts[k] > mx) {
        mx = counts[k];
        best = k;
      }
    });
    return mx > 0 ? best : ',';
  }

  function textToMatrix(text) {
    const lines = String(text || '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .filter(l => String(l).trim() !== '');
    if (!lines.length) return [];
    const delim = detectDelimiter(lines[0]);
    return lines.map(line => parseDelimitedLine(line, delim));
  }

  function normHeaderCell(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/\([^)]*\)/g, ' ')
      .replace(/_/g, ' ')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function inferColumnIndices(headerCells) {
    const norm = headerCells.map(c => normHeaderCell(c));
    let iN = -1;
    let iL = -1;
    let iW = -1;
    let iQ = -1;
    let iE = -1;
    let iG = -1;
    norm.forEach((t, i) => {
      if (/\b(panel\s*)?(name|label|reference|ref)\b/.test(t) || /\bpart\b/.test(t)) iN = i;
      if (/\bgroup\b/.test(t) || /^colour\s*#/.test(t)) iG = i;
      if (/\b(length|long)\b/.test(t) || t === 'l' || /\bl\b/.test(t)) {
        if (!/\bwidth\b/.test(t)) iL = i;
      }
      if (/\b(width|wide|breadth|depth)\b/.test(t) || t === 'w') iW = i;
      if (/\b(qty|quantity|quantities|pcs|pieces|amount|count)\b/.test(t) || t === 'q' || /^no$/.test(t)) iQ = i;
      if (/\b(edging|edges|machining)\b/.test(t) || /^\s*edge\b/.test(t) || /^\s*band\b/.test(t) || /^\s*tape\b/.test(t)) iE = i;
    });
    const hasHeader = iN >= 0 || iL >= 0 || iW >= 0 || iQ >= 0 || iE >= 0 || iG >= 0;
    return { iN, iL, iW, iQ, iE, iG, hasHeader };
  }

  function matrixToPanelRows(matrix) {
    if (!matrix.length) return [];
    const first = matrix[0].map(c => String(c).trim());
    let { iN, iL, iW, iQ, iE, hasHeader } = inferColumnIndices(first);
    let start = 0;
    const dimsLookNumeric =
      iL >= 0 && iW >= 0 && parseNumericMm(first[iL]) > 0 && parseNumericMm(first[iW]) > 0;
    if (hasHeader && iL >= 0 && iW >= 0 && !dimsLookNumeric) {
      start = 1;
    } else {
      start = 0;
      iN = -1;
      iL = 0;
      iW = 1;
      iQ = first.length > 2 ? 2 : -1;
      iE = first.length > 3 ? 3 : -1;
    }
    const rows = [];
    for (let r = start; r < matrix.length; r++) {
      const cells = matrix[r].map(c => String(c).trim());
      const need = Math.max(0, iN >= 0 ? iN : 0, iL, iW, iQ >= 0 ? iQ : 0, iE >= 0 ? iE : 0);
      while (cells.length <= need) cells.push('');
      const l = parseNumericMm(cells[iL] ?? '');
      const w = parseNumericMm(cells[iW] ?? '');
      const qRaw = iQ >= 0 ? cells[iQ] : '';
      const eRaw = iE >= 0 ? cells[iE] : '';
      const name = iN >= 0 ? String(cells[iN] || '').trim() : '';
      const q = qRaw === '' ? 1 : Math.max(1, toInt(qRaw, 1));
      const e = normalizeEdgeCodeForImport(eRaw);
      if (l > 0 && w > 0) rows.push({ name: name || `Panel ${rows.length + 1}`, l, w, q, e });
    }
    return rows;
  }

  function downloadBlob(blob, filename) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(a.href);
      a.remove();
    }, 2500);
  }

  let xlsxLoadPromise = null;
  function ensureXlsx() {
    if (global.XLSX) return Promise.resolve(global.XLSX);
    if (xlsxLoadPromise) return xlsxLoadPromise;
    xlsxLoadPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
      s.async = true;
      s.onload = () => {
        if (global.XLSX) resolve(global.XLSX);
        else reject(new Error('Excel library failed to initialise'));
      };
      s.onerror = () => reject(new Error('Could not load Excel library (network or blocker). Use CSV export instead.'));
      document.head.appendChild(s);
    });
    return xlsxLoadPromise;
  }

  async function workbookFileToMatrix(file) {
    const XLSX = await ensureXlsx();
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    let best = [];
    let bestLen = -1;
    wb.SheetNames.forEach(name => {
      const ws = wb.Sheets[name];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });
      if (!Array.isArray(data)) return;
      if (data.length > bestLen) {
        bestLen = data.length;
        best = data;
      }
    });
    return best;
  }

  async function parseImportFile(file) {
    const name = (file.name || '').toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      return matrixToPanelRows(await workbookFileToMatrix(file));
    }
    return matrixToPanelRows(textToMatrix(await file.text()));
  }

  /* Phone cut-list app (bd-cutlist-mobile.js): like parseImportFile, but keeps file row numbers,
     reports rows it could not read, and flags edging text it did not recognise. */
  function matrixToPanelRowsDetailed(matrix) {
    const out = { rows: [], hasGroupColumn: false, rejected: [] };
    while (matrix.length && !matrix[0].some(c => String(c).trim() !== '')) matrix = matrix.slice(1);
    if (!matrix.length) return out;
    const first = matrix[0].map(c => String(c).trim());
    let { iN, iL, iW, iQ, iE, hasHeader } = inferColumnIndices(first);
    let start = 0;
    const dimsLookNumeric = iL >= 0 && iW >= 0 && parseNumericMm(first[iL]) > 0 && parseNumericMm(first[iW]) > 0;
    if (hasHeader && iL >= 0 && iW >= 0 && !dimsLookNumeric) {
      start = 1;
    } else {
      iN = -1; iL = 0; iW = 1;
      iQ = first.length > 2 ? 2 : -1;
      iE = first.length > 3 ? 3 : -1;
    }
    for (let r = start; r < matrix.length; r++) {
      const cells = matrix[r].map(c => String(c).trim());
      if (!cells.some(c => c !== '')) continue;
      const l = parseNumericMm(cells[iL] ?? '');
      const w = parseNumericMm(cells[iW] ?? '');
      const qRaw = iQ >= 0 ? (cells[iQ] ?? '') : '';
      const eRaw = iE >= 0 ? (cells[iE] ?? '') : '';
      const name = iN >= 0 ? String(cells[iN] || '').trim() : '';
      const q = qRaw === '' ? 1 : Math.max(1, toInt(qRaw, 1));
      const e = normalizeEdgeCodeForImport(eRaw);
      if (l > 0 && w > 0) out.rows.push({ g: 1, name, l, w, q, e, line: r + 1, colour: '', eRaw, edgeUnknown: false });
      else out.rejected.push({ line: r + 1, name, cells, reason: !(l > 0) && !(w > 0) ? 'no length or width' : (!(l > 0) ? 'no length' : 'no width') });
    }
    return out;
  }

  async function parseImportFileDetailed(file) {
    const name = (file.name || '').toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) return matrixToPanelRowsDetailed(await workbookFileToMatrix(file));
    const lines = String(await file.text()).replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    const firstLine = lines.find(l => String(l).trim() !== '') || '';
    const delim = detectDelimiter(firstLine);
    return matrixToPanelRowsDetailed(lines.map(line => String(line).trim() === '' ? [] : parseDelimitedLine(line, delim)));
  }

  function exportCsv(tbody, filePrefix, hasEdging, defaultEdge) {
    const rows = getRowsFromTbody(tbody, !!hasEdging, defaultEdge || '4');
    if (!rows.length) return false;
    downloadBlob(new Blob([buildCsv(rows, !!hasEdging)], { type: 'text/csv;charset=utf-8' }), `${filePrefix || 'panel-cutlist'}.csv`);
    return true;
  }

  function getRowsFromTbody(tbody, hasEdging, defaultEdge) {
    const out = [];
    tbody.querySelectorAll('tr.pb-row').forEach((tr, index) => {
      const name = String(tr.querySelector('.pb-name')?.value || '').trim() || `Panel ${index + 1}`;
      const l = toInt(tr.querySelector('.pb-l')?.value, 0);
      const w = toInt(tr.querySelector('.pb-w')?.value, 0);
      const q = Math.max(1, toInt(tr.querySelector('.pb-q')?.value, 1));
      const eEl = tr.querySelector('.pb-e');
      const e = hasEdging && eEl ? String(eEl.value ?? defaultEdge) : defaultEdge;
      if (l > 0 && w > 0) out.push({ name, l, w, q, e });
    });
    return out;
  }

  function buildCsv(rows, hasEdging) {
    const header = hasEdging
      ? ['Panel_name', 'Length_mm', 'Width_mm', 'Qty', 'Edging_code', 'Edging_description']
      : ['Panel_name', 'Length_mm', 'Width_mm', 'Qty'];
    const lines = [header.join(',')];
    rows.forEach(r => {
      const name = String(r.name || '').replace(/"/g, '""');
      if (hasEdging) {
        const desc = String(EDGE_CODE_LABEL[r.e] || EDGE_CODE_LABEL['4']).replace(/"/g, '""');
        lines.push([`"${name}"`, r.l, r.w, r.q, r.e, `"${desc}"`].join(','));
      } else {
        lines.push([`"${name}"`, r.l, r.w, r.q].join(','));
      }
    });
    return `\uFEFF${lines.join('\r\n')}`;
  }

  async function exportXlsx(rows, filename, hasEdging) {
    const XLSX = await ensureXlsx();
    const aoa = hasEdging
      ? [
          ['Panel_name', 'Length_mm', 'Width_mm', 'Qty', 'Edging_code', 'Edging_description'],
          ...rows.map(r => [r.name || '', r.l, r.w, r.q, r.e, EDGE_CODE_LABEL[r.e] || EDGE_CODE_LABEL['4']])
        ]
      : [
          ['Panel_name', 'Length_mm', 'Width_mm', 'Qty'],
          ...rows.map(r => [r.name || '', r.l, r.w, r.q])
        ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cut list');
    XLSX.writeFile(wb, filename);
  }

  function applyRows(tbody, panelRows, makeRow, bindRow, hasEdging, defaultEdge) {
    tbody.innerHTML = '';
    panelRows.forEach((r, index) => {
      const tr = makeRow();
      const nameEl = tr.querySelector('.pb-name');
      if (nameEl) nameEl.value = String(r.name || `Panel ${index + 1}`);
      tr.querySelector('.pb-l').value = String(r.l);
      tr.querySelector('.pb-w').value = String(r.w);
      tr.querySelector('.pb-q').value = String(r.q);
      const eEl = tr.querySelector('.pb-e');
      if (hasEdging && eEl) eEl.value = r.e || defaultEdge;
      tbody.appendChild(tr);
      bindRow(tr);
    });
  }

  function wireSingle(opts) {
    const {
      tbody,
      makeRow,
      bindRow,
      afterChange,
      hasEdging = false,
      filePrefix = 'panel-cutlist',
      defaultEdge = '4',
      root = document
    } = opts || {};

    const wrap = (root.querySelector ? root : document).querySelector('[data-pb-cutlist-io-global]');
    if (!wrap || !tbody || typeof makeRow !== 'function' || typeof bindRow !== 'function') return;

    const st = wrap.querySelector('[data-pb-io-status-global]');
    const showStatus = (ok, msg) => {
      if (!st) return;
      if (!msg) {
        st.hidden = true;
        st.textContent = '';
        return;
      }
      st.hidden = false;
      st.textContent = msg;
      st.setAttribute('data-pb-io-ok', ok ? '1' : '0');
    };

    const refresh = () => {
      if (typeof afterChange === 'function') afterChange();
    };

    wrap.querySelector('[data-pb-export-all-csv]')?.addEventListener('click', () => {
      const rows = getRowsFromTbody(tbody, hasEdging, defaultEdge);
      if (!rows.length) {
        showStatus(false, 'Add at least one panel with length and width before downloading.');
        return;
      }
      downloadBlob(new Blob([buildCsv(rows, hasEdging)], { type: 'text/csv;charset=utf-8' }), `${filePrefix}.csv`);
      showStatus(true, 'CSV downloaded.');
    });

    wrap.querySelector('[data-pb-export-all-xlsx]')?.addEventListener('click', async () => {
      const rows = getRowsFromTbody(tbody, hasEdging, defaultEdge);
      if (!rows.length) {
        showStatus(false, 'Add at least one panel with length and width before downloading.');
        return;
      }
      try {
        await exportXlsx(rows, `${filePrefix}.xlsx`, hasEdging);
        showStatus(true, 'Excel file downloaded.');
      } catch (e) {
        showStatus(false, e?.message || 'Could not create Excel file. Try CSV instead.');
      }
    });

    wrap.querySelector('[data-pb-import-all-file]')?.addEventListener('change', async e => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      if (!global.confirm('Replace cut list rows using this file?')) return;
      try {
        const panelRows = await parseImportFile(file);
        if (!panelRows.length) {
          showStatus(false, 'No valid rows found. Each row needs length and width (mm).');
          return;
        }
        applyRows(tbody, panelRows, makeRow, bindRow, hasEdging, defaultEdge);
        refresh();
        showStatus(true, `Imported ${panelRows.length} row(s).`);
      } catch (err) {
        showStatus(false, err?.message || 'Could not read that file.');
      }
    });
  }

  global.PBCutlistIo = {
    wireSingle,
    EDGE_CODE_LABEL,
    ensureXlsx,
    parseImportFile,
    parseImportFileDetailed,
    exportCsv
  };
})(typeof window !== 'undefined' ? window : this);
